/**
 * The bundle's manifest (decision 7): the book's records as the store holds them, each text by the file that holds it.
 * Format version 1 (part 24a, table M) is fixed for good: part 24 imports it and every later version still reads it.
 * Version 2 (part 16c, table M2) is version 1's fields, then the book's images, and refused records of the image stores
 * with the file their binary went to. Version 3 (part 19d, table M3) is version 2's fields, then the book's character and
 * place notes, and refused records of the book notes' store too. Version 4 (part 23c, table B‴) is version 3's fields,
 * then the book's comment records, and refused records of the comments' store too. Version 5 (part 19e, table V5) is
 * version 2's fields with no chapter notes on the entries, then the book's note records (decision 76), and refused records
 * of the notes' store and of the stores the notes moved from. Version 6 (part 38d, table V6) is version 5's fields, each
 * note with the reading link's item it is (or null), then the book's reading-link records, and refused records of their
 * store too. Version 7 (part 38b, table BU) is version 6's fields, then the book's share records, and refused records of
 * their store too. Version 8 (part 38e, table BV) is version 7's fields, each note with its share part (or null), each
 * share with its role keys. Version 9 (part 15e, table TL) is version 8's fields, each chapter and trashed chapter with its
 * subtitle, the book with its template link (or null); it is what the app writes now.
 *
 * `manifestText` writes it; `readManifest` reads one back and refuses anything that isn't exactly a version 1 to 9
 * manifest, field by field, as an import must (Definition of Done, security: nothing unvalidated crosses into core
 * types).
 */
import { KEPT_SIDE } from '@cockatrice/core';
import type { ImageFormat } from '@cockatrice/core';
import { COMMENT_ORIGINS, CONFLICT_ORIGINS, isId, MAX_TEMPLATE_NAME, MAX_ZONE, MIN_ZONE, NOTE_KINDS, readNote, readShare, readShareLink, SNAPSHOT_REASONS, StoreRecordError } from '@cockatrice/storage';
import type { BookStoreName, CommentOrigin, CommentRange, ConflictOrigin, NoteAnchor, NoteKind, NoteLink, NoteRecord, NoteShare, ShareLinkRecord, ShareRecord, SnapshotPin, SnapshotReason, StoreName } from '@cockatrice/storage';

/**
 * M8: the stores a version 1 manifest names, fixed with it (part 24a). A store a later part adds (part 19c's book notes)
 * is none of them: the formats never widen with the local store.
 */
export const V1_STORES = Object.freeze(['chapters', 'entries', 'conflictCopies', 'snapshots', 'snapshotTexts', 'books', 'device'] as const satisfies readonly StoreName[]);

/** M8′: the stores a version 2 manifest names: version 1's, then the image stores (part 16c). */
export const V2_STORES = Object.freeze([...V1_STORES, 'images', 'imageBytes'] as const satisfies readonly BookStoreName[]);

/**
 * M8″: the stores a version 3 manifest names: version 1's, the book notes', then the image stores (part 19d): every store
 * a book is read from, in its order.
 */
export const V3_STORES = Object.freeze([...V1_STORES, 'bookNotes', 'images', 'imageBytes'] as const satisfies readonly BookStoreName[]);

/** B‴6: the stores a version 4 manifest names: version 3's, the comments' before the image stores (part 23c). */
export const V4_STORES = Object.freeze([...V1_STORES, 'bookNotes', 'comments', 'images', 'imageBytes'] as const satisfies readonly BookStoreName[]);

/**
 * V5-2, V5-3 (part 19e): the stores a version 5 manifest names: version 4's, the notes' before the image stores: every store
 * a book is read from, in its order.
 */
export const V5_STORES = Object.freeze([...V1_STORES, 'bookNotes', 'comments', 'notes', 'images', 'imageBytes'] as const satisfies readonly BookStoreName[]);

/** V6-2 (part 38d): the stores a version 6 manifest names: version 5's, the reading links' before the image stores. */
export const V6_STORES = Object.freeze([...V1_STORES, 'bookNotes', 'comments', 'notes', 'shareLinks', 'images', 'imageBytes'] as const satisfies readonly BookStoreName[]);

/**
 * BU (part 38b): the stores a version 7 manifest names: version 6's, the shares' before the image stores: every store a
 * book is read from, in its order. BV (part 38e): version 8 names the same (its role keys and share parts live in the
 * shares' and notes' records), and so does version 9 (part 15e: the templates' store is the library's, no book's).
 */
export const V7_STORES = Object.freeze([...V1_STORES, 'bookNotes', 'comments', 'notes', 'shareLinks', 'shares', 'images', 'imageBytes'] as const satisfies readonly BookStoreName[]);

/** A store a version 1 manifest names. */
export type V1Store = (typeof V1_STORES)[number];

/** A store a version 2 manifest names. */
export type V2Store = (typeof V2_STORES)[number];

/** A store a version 3 manifest names. */
export type V3Store = (typeof V3_STORES)[number];

/** A store a version 4 manifest names. */
export type V4Store = (typeof V4_STORES)[number];

/** A store a version 5 manifest names. */
export type V5Store = (typeof V5_STORES)[number];

/** A store a version 6 manifest names. */
export type V6Store = (typeof V6_STORES)[number];

/** A store a version 7 to 9 manifest names. */
export type V7Store = (typeof V7_STORES)[number];
import { jsonOf } from './json.ts';
import type { JsonValue } from './json.ts';
import { NOTE_FOLDERS } from './names.ts';

/** M1: what the manifest says it is. */
export const BUNDLE_FORMAT = 'cockatrice-bundle';

/** M1: the version the app writes (part 15e). */
export const BUNDLE_VERSION = 9;

/** M10′: the first version (part 24a), still read. */
export const BUNDLE_VERSION_1 = 1;

/** M10″: the second version (part 16c), still read. */
export const BUNDLE_VERSION_2 = 2;

/** B‴6: the third version (part 19d), still read. */
export const BUNDLE_VERSION_3 = 3;

/** V5-7: the fourth version (part 23c), still read. */
export const BUNDLE_VERSION_4 = 4;

/** V6-3: the fifth version (part 19e), still read. */
export const BUNDLE_VERSION_5 = 5;

/** BU: the sixth version (part 38d), still read. */
export const BUNDLE_VERSION_6 = 6;

/** BV5: the seventh version (part 38b), still read. */
export const BUNDLE_VERSION_7 = 7;

/** TL20: the eighth version (part 38e), still read. */
export const BUNDLE_VERSION_8 = 8;

/** M2: a text in the bundle: the file holding its UTF-8, and the text itself when the file can't give it back (T2). */
export interface TextFile {
  readonly file: string;
  readonly exact: string | null;
}

/** M3 (versions 1 to 8): the book record: null fields when the store holds none, or one it can't read (A2, A3). */
export interface ManifestBookV1 {
  readonly id: string;
  readonly createdAt: number | null;
  /** Part 05's `serializeMetadata` output, as stored. */
  readonly metadata: TextFile | null;
  /** Part 05's `serializeTheme` output, as stored. */
  readonly theme: TextFile | null;
}

/**
 * TL17–TL19 (part 15e): a book's template link: the ID the book names, and the name to give the template where it is
 * absent (a built-in's its ID). Its theme is the book's own, in theme.json, so it isn't repeated.
 */
export interface ManifestTemplate {
  readonly id: string;
  readonly name: string;
}

/** M3, TL17–TL20 (version 9): the book record, with its template link, or null for none. */
export interface ManifestBook extends ManifestBookV1 {
  readonly template: ManifestTemplate | null;
}

/** M4, V5-5 (versions 5 to 8): a chapter in the book: its entry's fields, and its text (null when it couldn't be read, A9). */
export interface ManifestChapterV5 {
  readonly id: string;
  readonly title: string;
  /** As stored, valid or not (decision 62). */
  readonly position: string;
  readonly synopsis: string;
  /** Its revision ID (decision 62): none before sync (part 26), so null in every bundle this part makes. */
  readonly revision: string | null;
  readonly text: TextFile | null;
}

/** M4 (version 9): a chapter, with its subtitle (part 15e). */
export interface ManifestChapter extends ManifestChapterV5 {
  readonly subtitle: string;
  /** T‴2: its own heading image, an image reference; empty for none. */
  readonly image: string;
}

/** M4 (versions 1 to 4): a chapter, with its notes on its entry (part 19e moved them to note records, MB1, MB2). */
export interface ManifestChapterV1 extends ManifestChapterV5 {
  readonly notes: string;
}

/** When and where a chapter went to the trash (M5). */
export interface Deletion {
  readonly at: number;
  readonly device: string;
}

/** M5 (version 9): a chapter in the trash: as a chapter, then when and where it was deleted. */
export interface ManifestTrashed extends ManifestChapter {
  readonly trashed: Deletion;
}

/** M5 (versions 5 to 8): a chapter in the trash with no subtitle. */
export interface ManifestTrashedV5 extends ManifestChapterV5 {
  readonly trashed: Deletion;
}

/** M5 (versions 1 to 4): a chapter in the trash with its notes. */
export interface ManifestTrashedV1 extends ManifestChapterV1 {
  readonly trashed: Deletion;
}

/** M6: a conflict copy. */
export interface ManifestCopy {
  readonly id: string;
  readonly chapterId: string;
  readonly createdAt: number;
  readonly origin: ConflictOrigin;
  readonly text: TextFile;
}

/** M7: a snapshot, with its owner, place, time and pins (decision 29), and its text (null when it has none to give). */
export interface ManifestSnapshot {
  readonly id: string;
  readonly chapterId: string;
  readonly owner: string;
  readonly seq: number;
  readonly createdAt: number;
  readonly settled: number | null;
  readonly zone: number;
  readonly reason: SnapshotReason;
  readonly pins: readonly SnapshotPin[];
  readonly text: TextFile | null;
}

/**
 * M8 (version 1): a record of the book the store holds but its reader refused, by its store and key (a key that isn't
 * text: null), with its value as stored, as JSON (`jsonOf`), and the text its key names elsewhere (*deep-fix r2*, table
 * R): a refused entry's chapter text (`damaged/<id>.md`), a refused snapshot's text; null when its key names none or
 * there is none.
 */
export interface UnreadableV1 {
  readonly store: V1Store;
  readonly key: string | null;
  readonly raw: JsonValue;
  readonly text: TextFile | null;
}

/**
 * M8′ (version 2): as version 1, from version 1's stores and the image stores (an image store's key is its ID part), then
 * the file the binary of a refused image-bytes record went to (table H, P10), or null.
 */
export interface UnreadableV2 {
  readonly store: V2Store;
  readonly key: string | null;
  readonly raw: JsonValue;
  readonly text: TextFile | null;
  readonly bytes: string | null;
}

/** M8″ (version 3): as version 2, from version 3's stores, the book notes' included (part 19d). */
export interface UnreadableV3 {
  readonly store: V3Store;
  readonly key: string | null;
  readonly raw: JsonValue;
  readonly text: TextFile | null;
  readonly bytes: string | null;
}

/** B‴2 (version 4): as version 3, from version 4's stores, the comments' included (part 23c). */
export interface UnreadableV4 {
  readonly store: V4Store;
  readonly key: string | null;
  readonly raw: JsonValue;
  readonly text: TextFile | null;
  readonly bytes: string | null;
}

/** V5-2, V5-3 (version 5): as version 4, from version 5's stores, the notes' included (part 19e). */
export interface UnreadableV5 {
  readonly store: V5Store;
  readonly key: string | null;
  readonly raw: JsonValue;
  readonly text: TextFile | null;
  readonly bytes: string | null;
}

/** V6-2 (version 6): as version 5, from version 6's stores, the reading links' included (part 38d). */
export interface UnreadableV6 {
  readonly store: V6Store;
  readonly key: string | null;
  readonly raw: JsonValue;
  readonly text: TextFile | null;
  readonly bytes: string | null;
}

/** BU (versions 7 to 9): as version 6, from any store a book is read from, the shares' included (part 38b). */
export interface Unreadable {
  readonly store: V7Store;
  readonly key: string | null;
  readonly raw: JsonValue;
  readonly text: TextFile | null;
  readonly bytes: string | null;
}

/** M11: an image record's fields as stored (part 16a, V1), but its book and ID. */
export interface ManifestImageRecord {
  readonly sha256: string;
  readonly format: ImageFormat;
  readonly width: number;
  readonly height: number;
  readonly size: number;
}

/** M11: an image of the book (table I): its record (null when refused or none) and its bytes' file (P9; null when none). */
export interface ManifestImage {
  readonly id: string;
  readonly record: ManifestImageRecord | null;
  readonly file: string | null;
}

/**
 * M12 (part 19d): a character or place note of the book (part 19c, K1): its ID, kind, name and creation time as stored,
 * and its text in a file of its kind's folder (P12).
 */
export interface ManifestNote {
  readonly id: string;
  readonly kind: NoteKind;
  readonly name: string;
  readonly text: TextFile;
  readonly createdAt: number;
}

/**
 * B‴1 (part 23c): a comment record of the book (table CR): its fields as stored but its book, and its text in a file of
 * its own (P13).
 */
export interface ManifestComment {
  readonly id: string;
  readonly chapterId: string;
  /** Deep-fix r1, ID′: the key a copy (L11) makes the comment's ID again from. */
  readonly sourceKey: string;
  readonly origin: CommentOrigin;
  readonly author: string;
  readonly createdAt: number | null;
  readonly storedAt: number;
  readonly range: CommentRange | null;
  readonly quote: string;
  readonly before: string;
  readonly after: string;
  readonly text: TextFile;
}

/**
 * V5-1 (part 19e): a note record of the book (table NR): every field as stored but its book, its text in a file of its own
 * (P14); in version 6 (V6-1), with the reading link's item it is, or null.
 */
export interface ManifestNoteRecordV5 {
  readonly id: string;
  readonly chapterId: string | null;
  readonly anchor: NoteAnchor | null;
  readonly title: string;
  readonly group: string;
  readonly origin: NoteRecord['origin'];
  readonly author: string;
  readonly sourceKey: string | null;
  readonly visibility: NoteRecord['visibility'];
  readonly parentId: string | null;
  readonly resolved: boolean;
  readonly writtenAt: number | null;
  readonly storedAt: number;
  readonly editedAt: number;
  readonly deletedAt: number | null;
  readonly text: TextFile;
}

/** V6-1 (part 38d): a note record of a version 6 or 7 manifest: version 5's, with its link (NR7). */
export interface ManifestNoteRecordV6 extends ManifestNoteRecordV5 {
  readonly link: NoteLink | null;
}

/** BV1 (part 38e): a note record of a version 8 or 9 manifest: version 6's, with its share part (NR8), or null for none. */
export interface ManifestNoteRecord extends ManifestNoteRecordV6 {
  readonly shared: NoteShare | null;
}

/** V6-1 (part 38d): a reading link's record (table SR): every field as stored but its book. */
export type ManifestShareLink = Omit<ShareLinkRecord, 'bookId'>;

/** BV1 (part 38e): a share's record (table SH): every field as stored but its book, its role keys included. */
export type ManifestShare = Omit<ShareRecord, 'bookId'>;

/** BU (part 38b): a share's record of a version 7 manifest: version 8's but its role keys (part 38e). */
export type ManifestShareV7 = Omit<ManifestShare, 'roleKeys'>;

/** What every version shares: what made the bundle. */
interface ManifestSource {
  readonly format: typeof BUNDLE_FORMAT;
  /** The device that made the bundle (decision 29: an import groups snapshots by it). */
  readonly device: string;
}

/** What versions 1 to 8 share: the book's records before the chapters. */
interface ManifestHead extends ManifestSource {
  readonly book: ManifestBookV1;
}

/** What versions 1 to 4 share: the book's records before the images, the chapters with their notes. */
interface ManifestRecords extends ManifestHead {
  readonly chapters: readonly ManifestChapterV1[];
  readonly trash: readonly ManifestTrashedV1[];
  readonly conflictCopies: readonly ManifestCopy[];
  readonly snapshots: readonly ManifestSnapshot[];
}

/** Table M: a version 1 manifest (part 24a), as read back. */
export interface ManifestV1 extends ManifestRecords {
  readonly version: typeof BUNDLE_VERSION_1;
  readonly unreadable: readonly UnreadableV1[];
}

/** Table M2: a version 2 manifest (part 16c), as read back. */
export interface ManifestV2 extends ManifestRecords {
  readonly version: typeof BUNDLE_VERSION_2;
  readonly images: readonly ManifestImage[];
  readonly unreadable: readonly UnreadableV2[];
}

/** Table M3: a version 3 manifest (part 19d), as read back. */
export interface ManifestV3 extends ManifestRecords {
  readonly version: typeof BUNDLE_VERSION_3;
  readonly images: readonly ManifestImage[];
  readonly bookNotes: readonly ManifestNote[];
  readonly unreadable: readonly UnreadableV3[];
}

/** Table B‴: a version 4 manifest (part 23c), as read back. */
export interface ManifestV4 extends ManifestRecords {
  readonly version: typeof BUNDLE_VERSION_4;
  readonly images: readonly ManifestImage[];
  readonly bookNotes: readonly ManifestNote[];
  readonly comments: readonly ManifestComment[];
  readonly unreadable: readonly UnreadableV4[];
}

/** Table V5: a version 5 manifest (part 19e), as read back. */
export interface ManifestV5 extends ManifestHead {
  readonly version: typeof BUNDLE_VERSION_5;
  readonly chapters: readonly ManifestChapterV5[];
  readonly trash: readonly ManifestTrashedV5[];
  readonly conflictCopies: readonly ManifestCopy[];
  readonly snapshots: readonly ManifestSnapshot[];
  readonly images: readonly ManifestImage[];
  readonly notes: readonly ManifestNoteRecordV5[];
  readonly unreadable: readonly UnreadableV5[];
}

/** Table V6: a version 6 manifest (part 38d), as read back. */
export interface ManifestV6 extends ManifestHead {
  readonly version: typeof BUNDLE_VERSION_6;
  readonly chapters: readonly ManifestChapterV5[];
  readonly trash: readonly ManifestTrashedV5[];
  readonly conflictCopies: readonly ManifestCopy[];
  readonly snapshots: readonly ManifestSnapshot[];
  readonly images: readonly ManifestImage[];
  readonly notes: readonly ManifestNoteRecordV6[];
  readonly shareLinks: readonly ManifestShareLink[];
  readonly unreadable: readonly UnreadableV6[];
}

/** Table BU: a version 7 manifest (part 38b), as read back. */
export interface ManifestV7 extends ManifestHead {
  readonly version: typeof BUNDLE_VERSION_7;
  readonly chapters: readonly ManifestChapterV5[];
  readonly trash: readonly ManifestTrashedV5[];
  readonly conflictCopies: readonly ManifestCopy[];
  readonly snapshots: readonly ManifestSnapshot[];
  readonly images: readonly ManifestImage[];
  readonly notes: readonly ManifestNoteRecordV6[];
  readonly shareLinks: readonly ManifestShareLink[];
  readonly shares: readonly ManifestShareV7[];
  readonly unreadable: readonly Unreadable[];
}

/** Table BV: a version 8 manifest (part 38e), as read back. */
export interface ManifestV8 extends ManifestHead {
  readonly version: typeof BUNDLE_VERSION_8;
  readonly chapters: readonly ManifestChapterV5[];
  readonly trash: readonly ManifestTrashedV5[];
  readonly conflictCopies: readonly ManifestCopy[];
  readonly snapshots: readonly ManifestSnapshot[];
  readonly images: readonly ManifestImage[];
  readonly notes: readonly ManifestNoteRecord[];
  readonly shareLinks: readonly ManifestShareLink[];
  readonly shares: readonly ManifestShare[];
  readonly unreadable: readonly Unreadable[];
}

/** Table TL: the manifest the app writes, version 9 (part 15e). */
export interface Manifest extends ManifestSource {
  readonly version: typeof BUNDLE_VERSION;
  readonly book: ManifestBook;
  readonly chapters: readonly ManifestChapter[];
  readonly trash: readonly ManifestTrashed[];
  readonly conflictCopies: readonly ManifestCopy[];
  readonly snapshots: readonly ManifestSnapshot[];
  readonly images: readonly ManifestImage[];
  readonly notes: readonly ManifestNoteRecord[];
  readonly shareLinks: readonly ManifestShareLink[];
  readonly shares: readonly ManifestShare[];
  readonly unreadable: readonly Unreadable[];
}

/** Any manifest {@link readManifest} reads. */
export type AnyManifest = Manifest | ManifestV8 | ManifestV7 | ManifestV6 | ManifestV5 | ManifestV4 | ManifestV3 | ManifestV2 | ManifestV1;

/** M9: the manifest as its file holds it: JSON, two-space indents, a line end after, fields in the order given. */
export function manifestText(manifest: AnyManifest): string {
  return `${JSON.stringify(manifest, null, 2)}\n`;
}

/** Thrown by {@link readManifest} for anything but a version 1 to 9 manifest; `field` is where, as a path. */
export class ManifestError extends Error {
  override readonly name = 'ManifestError';
  readonly field: string;

  constructor(field: string, problem: string) {
    super(`${field === '' ? 'the manifest' : field}: ${problem}`);
    this.field = field;
  }
}

/** M2: a path a text's file may have: one the bundle makes (table P). */
const TEXT_PATH = /^(?:chapters|trash|damaged|conflict-copies|snapshots\/[0-9a-f]{32})\/[a-z0-9-]+\.md$|^(?:metadata|theme)\.json$/u;

/** M11: an image's file (P9, P11), its ID captured. */
const IMAGE_PATH = /^images\/([0-9a-f]{32})\.(?:jpg|png|webp|bin)$/u;

/** M12: a note's text (P12): its kind's folder captured. */
const NOTE_PATH = /^(characters|places)\/[a-z0-9-]+\.md$/u;

/** B‴5: a comment's text (P13). */
const COMMENT_PATH = /^comments\/[0-9]{3,}\.txt$/u;

/** V5-1: a note's text (P14). */
const NOTE_RECORD_PATH = /^notes\/[0-9]{3,}\.txt$/u;

/** M8′: a refused image-bytes record's binary (P10, P11). */
const DAMAGED_IMAGE_PATH = /^damaged-images\/[0-9]{3,}\.(?:jpg|png|webp|bin)$/u;

/** M11: an image record's formats (part 16a, V1). */
const IMAGE_FORMATS: readonly ImageFormat[] = ['jpeg', 'png'];

/** Whether `value` is a whole number a double holds exactly. */
function isWhole(value: unknown): value is number {
  return Number.isSafeInteger(value);
}

/** An object's own fields, each read once, with errors named from its path (part 24b reads its pieces with it too). */
export class Fields {
  readonly path: string;
  private readonly values: ReadonlyMap<string, unknown>;

  constructor(path: string, values: ReadonlyMap<string, unknown>) {
    this.path = path;
    this.values = values;
  }

  /** The error name of `key`. */
  name(key: string): string {
    return this.path === '' ? key : `${this.path}.${key}`;
  }

  get(key: string): unknown {
    return this.values.get(key);
  }

  string(key: string): string {
    const value = this.values.get(key);
    if (typeof value !== 'string') throw new ManifestError(this.name(key), 'must be text');
    return value;
  }

  id(key: string): string {
    const value = this.values.get(key);
    if (!isId(value)) throw new ManifestError(this.name(key), 'must be 32 lowercase hex digits');
    return value;
  }

  /** A whole number from 0 (-0 reads as 0). */
  count(key: string): number {
    const value = this.values.get(key);
    if (!isWhole(value) || value < 0) throw new ManifestError(this.name(key), 'must be a whole number from 0');
    return value + 0;
  }

  /** A whole number from `min` to `max`. */
  within(key: string, min: number, max: number): number {
    const value = this.count(key);
    if (value < min || value > max) throw new ManifestError(this.name(key), `must be from ${String(min)} to ${String(max)}`);
    return value;
  }

  /** A whole number from 0, or null. */
  countOrNull(key: string): number | null {
    return this.values.get(key) === null ? null : this.count(key);
  }

  list(key: string): readonly unknown[] {
    const value = this.values.get(key);
    if (!Array.isArray(value)) throw new ManifestError(this.name(key), 'must be a list');
    return value;
  }
}

/** M10: the fields of a JSON object at `path` holding exactly `keys`; part 24b's pieces are read with it too. */
export function fieldsOf(value: unknown, path: string, keys: readonly string[]): Fields {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new ManifestError(path, 'must be an object');
  const values = new Map(Object.entries(value));
  for (const key of values.keys()) if (!keys.includes(key)) throw new ManifestError(path, `has an unknown field "${key.slice(0, 64)}"`);
  for (const key of keys) if (!values.has(key)) throw new ManifestError(path === '' ? key : `${path}.${key}`, 'is missing');
  return new Fields(path, values);
}

/** M2: a text's file, at `path`, its file one `paths` matches (table P's texts unless given). */
function readTextFile(value: unknown, path: string, paths: RegExp = TEXT_PATH): TextFile {
  const fields = fieldsOf(value, path, ['file', 'exact']);
  const file = fields.string('file');
  if (!paths.test(file)) throw new ManifestError(fields.name('file'), 'must be a path the bundle makes');
  const exact = fields.get('exact');
  return { file, exact: exact === null ? null : fields.string('exact') };
}

/** M2: a text's file or null. */
function readTextFileOrNull(fields: Fields, key: string): TextFile | null {
  const value = fields.get(key);
  return value === null ? null : readTextFile(value, fields.name(key));
}

const BOOK_KEYS_V1 = ['id', 'createdAt', 'metadata', 'theme'];

const BOOK_KEYS = [...BOOK_KEYS_V1, 'template'];

/** M3: the fields every version's book holds. */
function bookFields(fields: Fields): ManifestBookV1 {
  return {
    id: fields.id('id'),
    createdAt: fields.countOrNull('createdAt'),
    metadata: readTextFileOrNull(fields, 'metadata'),
    theme: readTextFileOrNull(fields, 'theme'),
  };
}

/** M3 (versions 1 to 8). */
function readBookV1(value: unknown): ManifestBookV1 {
  return bookFields(fieldsOf(value, 'book', BOOK_KEYS_V1));
}

/** TL17: the most a link holds: a template's ID, or a built-in's (the store's reader's limit, `templateLink`). */
const MAX_TEMPLATE_ID = 64;

/** TL17–TL20: a book's template link at `path`, or null for none (part 24b reads a piece's with it). */
export function readTemplateLink(value: unknown, path: string): ManifestTemplate | null {
  if (value === null) return null;
  const fields = fieldsOf(value, path, ['id', 'name']);
  const id = fields.string('id');
  if (id === '' || id.length > MAX_TEMPLATE_ID) throw new ManifestError(fields.name('id'), `must be 1 to ${String(MAX_TEMPLATE_ID)} characters`);
  const name = fields.string('name');
  if (name.length > MAX_TEMPLATE_NAME) throw new ManifestError(fields.name('name'), `must be at most ${String(MAX_TEMPLATE_NAME)} characters`);
  return { id, name };
}

/** M3, TL17–TL20 (version 9). */
function readBook(value: unknown): ManifestBook {
  const fields = fieldsOf(value, 'book', BOOK_KEYS);
  return { ...bookFields(fields), template: readTemplateLink(fields.get('template'), fields.name('template')) };
}

/** M4, V5-5 (versions 5 to 8): a chapter's fields but its text. */
const ENTRY_KEYS_V5 = ['id', 'title', 'position', 'synopsis', 'revision'];

/** M4 (version 9): a chapter's fields but its text and, in versions 1 to 4, its notes: version 5's, then its subtitle. */
export const ENTRY_KEYS = [...ENTRY_KEYS_V5, 'subtitle', 'image'];

/** M4 (versions 1 to 4): a chapter's fields but its text, its notes among them. */
const ENTRY_KEYS_V1 = ['id', 'title', 'position', 'synopsis', 'notes', 'revision'];

/** M4, M5: the fields every version's chapter and chapter in the trash share, but the text, notes and subtitle. */
function sharedEntryFields(fields: Fields): Omit<ManifestChapterV5, 'text'> {
  const revision = fields.get('revision');
  return {
    id: fields.id('id'),
    title: fields.string('title'),
    position: fields.string('position'),
    synopsis: fields.string('synopsis'),
    revision: revision === null ? null : fields.string('revision'),
  };
}

/** M4, M5 (version 9): a chapter's fields but its text and notes, its subtitle among them (part 24b reads a piece's with it). */
export function entryFields(fields: Fields): Omit<ManifestChapter, 'text'> {
  const image = fields.string('image');
  // T‴: what a bundle says is the worker's to lay out: only an image reference, or nothing.
  if (image !== '' && !/^image:[0-9a-f]{32}\?sha256=[0-9a-f]{64}$/u.test(image)) throw new ManifestError(fields.name('image'), 'must be an image reference or empty');
  return { ...sharedEntryFields(fields), subtitle: fields.string('subtitle'), image };
}

/** M4 (version 9): a chapter. */
function readChapter(value: unknown, path: string): ManifestChapter {
  const fields = fieldsOf(value, path, [...ENTRY_KEYS, 'text']);
  return { ...entryFields(fields), text: readTextFileOrNull(fields, 'text') };
}

/** V5-5 (versions 5 to 8): a chapter with no subtitle. */
function readChapterV5(value: unknown, path: string): ManifestChapterV5 {
  const fields = fieldsOf(value, path, [...ENTRY_KEYS_V5, 'text']);
  return { ...sharedEntryFields(fields), text: readTextFileOrNull(fields, 'text') };
}

/** M4 (versions 1 to 4): a chapter with its notes. */
function readChapterV1(value: unknown, path: string): ManifestChapterV1 {
  const fields = fieldsOf(value, path, [...ENTRY_KEYS_V1, 'text']);
  const { id, title, position, synopsis, revision } = sharedEntryFields(fields);
  return { id, title, position, synopsis, notes: fields.string('notes'), revision, text: readTextFileOrNull(fields, 'text') };
}

/** M5: when and where a chapter went to the trash, at `path`. */
export function readTrashedField(value: unknown, path: string): ManifestTrashed['trashed'] {
  const trashed = fieldsOf(value, path, ['at', 'device']);
  return { at: trashed.count('at'), device: trashed.id('device') };
}

/** M5 (version 9). */
function readTrashed(value: unknown, path: string): ManifestTrashed {
  const fields = fieldsOf(value, path, [...ENTRY_KEYS, 'text', 'trashed']);
  return { ...entryFields(fields), text: readTextFileOrNull(fields, 'text'), trashed: readTrashedField(fields.get('trashed'), fields.name('trashed')) };
}

/** M5, V5-5 (versions 5 to 8). */
function readTrashedV5(value: unknown, path: string): ManifestTrashedV5 {
  const fields = fieldsOf(value, path, [...ENTRY_KEYS_V5, 'text', 'trashed']);
  return { ...sharedEntryFields(fields), text: readTextFileOrNull(fields, 'text'), trashed: readTrashedField(fields.get('trashed'), fields.name('trashed')) };
}

/** M5 (versions 1 to 4): with its notes. */
function readTrashedV1(value: unknown, path: string): ManifestTrashedV1 {
  const fields = fieldsOf(value, path, [...ENTRY_KEYS_V1, 'text', 'trashed']);
  const { id, title, position, synopsis, revision } = sharedEntryFields(fields);
  return { id, title, position, synopsis, notes: fields.string('notes'), revision, text: readTextFileOrNull(fields, 'text'), trashed: readTrashedField(fields.get('trashed'), fields.name('trashed')) };
}

/** M6: a copy's fields. */
export const COPY_KEYS = ['id', 'chapterId', 'createdAt', 'origin', 'text'];

/** M6: a copy's fields but its text (part 24b reads a piece's with it). */
export function copyFields(fields: Fields): Omit<ManifestCopy, 'text'> {
  // Part 23b: a re-import's copy (L9) as well as a stale tab's (L1), in every version: no bundle before held either kind.
  const origin = CONFLICT_ORIGINS.find((known) => known === fields.get('origin'));
  if (origin === undefined) throw new ManifestError(fields.name('origin'), `must be one of ${CONFLICT_ORIGINS.map((known) => `"${known}"`).join(', ')}`);
  return { id: fields.id('id'), chapterId: fields.id('chapterId'), createdAt: fields.count('createdAt'), origin };
}

/** M6. */
function readCopy(value: unknown, path: string): ManifestCopy {
  const fields = fieldsOf(value, path, COPY_KEYS);
  return { ...copyFields(fields), text: readTextFile(fields.get('text'), fields.name('text')) };
}

/** M7: one pin. */
export function readPin(value: unknown, path: string): SnapshotPin {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'share-link') return { kind, linkId: fieldsOf(value, path, ['kind', 'linkId']).id('linkId') };
  if (kind === 'docx-export' || kind === 'bundle') return { kind, until: fieldsOf(value, path, ['kind', 'until']).count('until') };
  throw new ManifestError(path, 'must be a share-link, docx-export or bundle pin');
}

/** M7: a snapshot's fields but its text, the zone a whole number of minutes from UTC−12:00 to UTC+14:00 (part 24b reads a piece's with it). */
export function snapshotFields(fields: Fields): Omit<ManifestSnapshot, 'text'> {
  const zone = fields.get('zone');
  if (!isWhole(zone) || zone < MIN_ZONE || zone > MAX_ZONE) {
    throw new ManifestError(fields.name('zone'), `must be a whole number of minutes from ${String(MIN_ZONE)} to ${String(MAX_ZONE)}`);
  }
  const reason = SNAPSHOT_REASONS.find((known) => known === fields.get('reason'));
  if (reason === undefined) throw new ManifestError(fields.name('reason'), `must be one of ${SNAPSHOT_REASONS.join(', ')}`);
  return {
    id: fields.id('id'),
    chapterId: fields.id('chapterId'),
    owner: fields.id('owner'),
    seq: fields.count('seq'),
    createdAt: fields.count('createdAt'),
    settled: fields.countOrNull('settled'),
    zone: zone + 0,
    reason,
    pins: fields.list('pins').map((pin, index) => readPin(pin, `${fields.name('pins')}[${String(index)}]`)),
  };
}

/** The fields of a snapshot. */
export const SNAPSHOT_KEYS = ['id', 'chapterId', 'owner', 'seq', 'createdAt', 'settled', 'zone', 'reason', 'pins', 'text'];

/** M7. */
function readSnapshot(value: unknown, path: string): ManifestSnapshot {
  const fields = fieldsOf(value, path, SNAPSHOT_KEYS);
  return { ...snapshotFields(fields), text: readTextFileOrNull(fields, 'text') };
}

/** M8, M8′: the store of a refused record, one of `names` (part 24b reads a piece's with it). */
export function readStore<S extends string>(fields: Fields, names: readonly S[]): S {
  const store = names.find((name) => name === fields.get('store'));
  if (store === undefined) throw new ManifestError(fields.name('store'), `must be one of ${names.join(', ')}`);
  return store;
}

/** M8, M8′: what a refused record's entry holds in both versions: its key, its raw value and the text its key names. */
function refusedFields(fields: Fields): Pick<UnreadableV1, 'key' | 'raw' | 'text'> {
  const key = fields.get('key');
  // M8: the raw value is whatever was stored, as JSON: parsed JSON, taken as it is.
  return { key: key === null ? null : fields.string('key'), raw: jsonOf(fields.get('raw')), text: readTextFileOrNull(fields, 'text') };
}

/** M8 (version 1). */
function readUnreadableV1(value: unknown, path: string): UnreadableV1 {
  const fields = fieldsOf(value, path, ['store', 'key', 'raw', 'text']);
  return { store: readStore(fields, V1_STORES), ...refusedFields(fields) };
}

/** M8′, M8″, B‴2, V5-2, V6-2, BU (versions 2 to 7): a refused record of one of `stores`, with its binary's file. */
function readUnreadableOf<S extends V7Store>(stores: readonly S[]): (value: unknown, path: string) => { store: S; key: string | null; raw: JsonValue; text: TextFile | null; bytes: string | null } {
  return (value, path) => {
    const fields = fieldsOf(value, path, ['store', 'key', 'raw', 'text', 'bytes']);
    const store = readStore(fields, stores);
    const refused = refusedFields(fields);
    const bytes = fields.get('bytes') === null ? null : fields.string('bytes');
    if (bytes !== null && !DAMAGED_IMAGE_PATH.test(bytes)) throw new ManifestError(fields.name('bytes'), 'must be a path the bundle makes');
    return { store, ...refused, bytes };
  };
}

/** M12: a note's fields. */
export const NOTE_KEYS = ['id', 'kind', 'name', 'text', 'createdAt'];

/** M12: a note's kind (part 24b reads a piece's with it). */
export function readNoteKind(fields: Fields): ManifestNote['kind'] {
  const kind = NOTE_KINDS.find((known) => known === fields.get('kind'));
  if (kind === undefined) throw new ManifestError(fields.name('kind'), `must be one of ${NOTE_KINDS.join(', ')}`);
  return kind;
}

/** M12: a book note, its text in its own kind's folder. */
function readBookNote(value: unknown, path: string): ManifestNote {
  const fields = fieldsOf(value, path, NOTE_KEYS);
  const id = fields.id('id');
  const kind = readNoteKind(fields);
  const name = fields.string('name');
  const text = readTextFile(fields.get('text'), fields.name('text'), NOTE_PATH);
  if (NOTE_PATH.exec(text.file)?.[1] !== NOTE_FOLDERS[kind]) throw new ManifestError(`${fields.name('text')}.file`, `must be in ${NOTE_FOLDERS[kind]}/`);
  return { id, kind, name, text, createdAt: fields.count('createdAt') };
}

/** M12, M13: the notes, no ID twice (each is the key of the record it came from). */
function readNotes(fields: Fields): ManifestNote[] {
  const seen = new Set<string>();
  return readList(fields, 'bookNotes', (value, path) => {
    const note = readBookNote(value, path);
    if (seen.has(note.id)) throw new ManifestError(`${path}.id`, 'must not be an earlier note’s ID');
    seen.add(note.id);
    return note;
  });
}

/** A SHA-256 as 64 lowercase hex digits (M11's hash, B‴1's key). */
function readHash(fields: Fields, key: string): string {
  const value = fields.get(key);
  if (typeof value !== 'string' || !/^[0-9a-f]{64}$/u.test(value)) throw new ManifestError(fields.name(key), 'must be 64 lowercase hex digits');
  return value;
}

/** B‴1: a comment's range, by the store's reader's rule (CR1): two whole numbers, the first not past the second. */
function readRange(value: unknown, path: string): CommentRange {
  const fields = fieldsOf(value, path, ['from', 'to']);
  const from = fields.count('from');
  const to = fields.count('to');
  if (to < from) throw new ManifestError(fields.name('to'), 'must not be before from');
  return { from, to };
}

/** B‴1: a comment's fields. */
export const COMMENT_KEYS = ['id', 'chapterId', 'sourceKey', 'origin', 'author', 'createdAt', 'storedAt', 'range', 'quote', 'before', 'after', 'text'];

/** B‴1: a comment's fields but its text (part 24b reads a piece's with it). */
export function commentFields(fields: Fields): Omit<ManifestComment, 'text'> {
  const origin = COMMENT_ORIGINS.find((known) => known === fields.get('origin'));
  if (origin === undefined) throw new ManifestError(fields.name('origin'), `must be one of ${COMMENT_ORIGINS.join(', ')}`);
  const range = fields.get('range');
  return {
    id: fields.id('id'),
    chapterId: fields.id('chapterId'),
    sourceKey: readHash(fields, 'sourceKey'),
    origin,
    author: fields.string('author'),
    createdAt: fields.countOrNull('createdAt'),
    storedAt: fields.count('storedAt'),
    range: range === null ? null : readRange(range, fields.name('range')),
    quote: fields.string('quote'),
    before: fields.string('before'),
    after: fields.string('after'),
  };
}

/** B‴1: a comment, its text in the comments' folder. */
function readComment(value: unknown, path: string): ManifestComment {
  const fields = fieldsOf(value, path, COMMENT_KEYS);
  return { ...commentFields(fields), text: readTextFile(fields.get('text'), fields.name('text'), COMMENT_PATH) };
}

/** B‴1, B‴6 (M15): the comments, no ID twice (each is the key of the record it came from). */
function readComments(fields: Fields): ManifestComment[] {
  const seen = new Set<string>();
  return readList(fields, 'comments', (value, path) => {
    const comment = readComment(value, path);
    if (seen.has(comment.id)) throw new ManifestError(`${path}.id`, 'must not be an earlier comment’s ID');
    seen.add(comment.id);
    return comment;
  });
}

/** V5-1: a version 5 note record's fields. */
export const NOTE_RECORD_KEYS_V5 = ['id', 'chapterId', 'anchor', 'title', 'group', 'origin', 'author', 'sourceKey', 'visibility', 'parentId', 'resolved', 'writtenAt', 'storedAt', 'editedAt', 'deletedAt', 'text'];

/** V6-1: a version 6 or 7 note record's fields: version 5's, its link before its text. */
export const NOTE_RECORD_KEYS_V6 = [...NOTE_RECORD_KEYS_V5.slice(0, -1), 'link', 'text'];

/** BV1: a note record's fields: version 6's, its share part before its text. */
export const NOTE_RECORD_KEYS = [...NOTE_RECORD_KEYS_V6.slice(0, -1), 'shared', 'text'];

/** V5-1, V6-1, BV1: a stored note record's fields as the manifest lists them, but its text (which goes in a file of its own). */
export function noteFields(note: NoteRecord): Omit<ManifestNoteRecord, 'text'> {
  const { anchor, shared } = note;
  return {
    id: note.id,
    chapterId: note.chapterId,
    anchor: anchor === null ? null : { range: anchor.range === null ? null : { from: anchor.range.from, to: anchor.range.to }, quote: anchor.quote, before: anchor.before, after: anchor.after },
    title: note.title,
    group: note.group,
    origin: note.origin,
    author: note.author,
    sourceKey: note.sourceKey,
    visibility: note.visibility,
    parentId: note.parentId,
    resolved: note.resolved,
    writtenAt: note.writtenAt,
    storedAt: note.storedAt,
    editedAt: note.editedAt,
    deletedAt: note.deletedAt,
    link: note.link === undefined ? null : { link: note.link.link, thread: note.link.thread, item: note.link.item },
    // Deep-fix r5: the range a note went with (MP3) travels too, so an imported note the relay holds isn't sent again.
    shared:
      shared === undefined
        ? null
        : { share: shared.share, note: shared.note, thread: shared.thread, author: shared.author, epoch: shared.epoch, sent: shared.sent, ...(shared.range === undefined ? {} : { range: { from: shared.range.from, to: shared.range.to } }) },
  };
}

/**
 * V5-6, V6-1, BV1: a note record's fields but its text, by the store's reader's rules (table NR, `readNote`; a link or
 * share part null is none), its words checked against none for a tombstone once its text is read (part 24b reads a
 * piece's with it, its text then known). A version 5 note has no link; a version 5 to 7 note no share part, so NR8 reads
 * it as Only me and never an invitee's (BV5).
 */
export function noteRecordFields(fields: Fields, text = ''): Omit<ManifestNoteRecord, 'text'> {
  const record: Record<string, unknown> = { bookId: '0'.repeat(32), text };
  for (const key of NOTE_RECORD_KEYS_V5) if (key !== 'text') record[key] = fields.get(key);
  for (const key of ['link', 'shared']) {
    const value = fields.get(key);
    if (value !== undefined && value !== null) record[key] = value;
  }
  let note: NoteRecord;
  try {
    note = readNote(record);
  } catch (error) {
    throw manifestErrorOf(error, fields);
  }
  return noteFields(note);
}

/**
 * V5-6: the store's reader's refusal `error` as the manifest's, at `fields`' path: a `StoreRecordError` naming its field
 * there with its problem; anything else (no reader throws it) as it is.
 */
export function manifestErrorOf(error: unknown, fields: Fields): unknown {
  if (!(error instanceof StoreRecordError)) return error;
  const { field, message } = error;
  return field === null ? new ManifestError(fields.path, message) : new ManifestError(fields.name(field), message.slice(field.length + 2));
}

/** V6-3: a note of a version 5 manifest as it reads: every field but the link, which came with version 6. */
function noteRecordV5(note: ManifestNoteRecord): ManifestNoteRecordV5 {
  const { id, chapterId, anchor, title, group, origin, author, sourceKey, visibility, parentId, resolved, writtenAt, storedAt, editedAt, deletedAt, text } = note;
  return { id, chapterId, anchor, title, group, origin, author, sourceKey, visibility, parentId, resolved, writtenAt, storedAt, editedAt, deletedAt, text };
}

/** BV5: a note of a version 6 or 7 manifest as it reads: every field but the share part, which came with version 8. */
function noteRecordV6(note: ManifestNoteRecord): ManifestNoteRecordV6 {
  const { id, chapterId, anchor, title, group, origin, author, sourceKey, visibility, parentId, resolved, writtenAt, storedAt, editedAt, deletedAt, link, text } = note;
  return { id, chapterId, anchor, title, group, origin, author, sourceKey, visibility, parentId, resolved, writtenAt, storedAt, editedAt, deletedAt, link, text };
}

/**
 * V5-1, V6-1, BV1: a note record of a version 8 manifest (`keys` {@link NOTE_RECORD_KEYS}), 6 or 7
 * ({@link NOTE_RECORD_KEYS_V6}) or 5, its text in the notes' folder.
 */
function readNoteRecord(value: unknown, path: string, keys: readonly string[]): ManifestNoteRecord {
  const fields = fieldsOf(value, path, keys);
  // V6-3: a reader's note came with version 6.
  if (keys === NOTE_RECORD_KEYS_V5 && fields.get('origin') === 'reader') throw new ManifestError(fields.name('origin'), 'must be one of writer, word');
  return { ...noteRecordFields(fields), text: readTextFile(fields.get('text'), fields.name('text'), NOTE_RECORD_PATH) };
}

/**
 * V5-6: the note records, no ID twice (each is the key of the record it came from), every reply's parent a thread's first
 * note of the bundle on the same chapter.
 */
function readNoteRecords(fields: Fields, keys: readonly string[]): ManifestNoteRecord[] {
  const seen = new Map<string, ManifestNoteRecord>();
  const notes = readList(fields, 'notes', (value, path) => {
    const note = readNoteRecord(value, path, keys);
    if (seen.has(note.id)) throw new ManifestError(`${path}.id`, 'must not be an earlier note’s ID');
    seen.set(note.id, note);
    return note;
  });
  notes.forEach((note, index) => {
    if (note.parentId === null) return;
    const parent = seen.get(note.parentId);
    if (parent?.parentId !== null || parent.chapterId !== note.chapterId) {
      throw new ManifestError(`${fields.name('notes')}[${String(index)}].parentId`, 'must be a first note of the bundle on the same chapter');
    }
  });
  return notes;
}

/** V6-1: a reading link's fields. */
export const SHARE_LINK_KEYS = ['id', 'name', 'key', 'secret', 'chapters', 'snapshots', 'comments', 'expiresAt', 'createdAt', 'size', 'uploaded', 'ended'];

/** V6-1: a stored reading link's fields as the manifest lists them, in its order. */
export function shareLinkFields(link: ShareLinkRecord): ManifestShareLink {
  const { id, name, key, secret, chapters, snapshots, comments, expiresAt, createdAt, size, uploaded, ended } = link;
  return { id, name, key, secret, chapters: [...chapters], snapshots: [...snapshots], comments, expiresAt, createdAt, size, uploaded, ended: ended === null ? null : { at: ended.at, why: ended.why } };
}

/** V6-4: a reading link of a manifest as book `bookId` holds it. */
export function shareLinkRecordOf(link: ManifestShareLink, bookId: string): ShareLinkRecord {
  const { id, name, key, secret, chapters, snapshots, comments, expiresAt, createdAt, size, uploaded, ended } = link;
  return { id, bookId, name, key, secret, chapters, snapshots, comments, expiresAt, createdAt, size, uploaded, ended };
}

/** V6-1: a reading link's fields, by the store's reader's rules (table SR, `readShareLink`; part 24b reads a piece's with it). */
export function shareLinkRecordFields(fields: Fields): ManifestShareLink {
  const record: Record<string, unknown> = { bookId: '0'.repeat(32) };
  for (const key of SHARE_LINK_KEYS) record[key] = fields.get(key);
  try {
    return shareLinkFields(readShareLink(record));
  } catch (error) {
    throw manifestErrorOf(error, fields);
  }
}

/** V6-1: the reading links, no ID twice (each is the key of the record it came from). */
function readShareLinks(fields: Fields): ManifestShareLink[] {
  const seen = new Set<string>();
  return readList(fields, 'shareLinks', (value, path) => {
    const link = shareLinkRecordFields(fieldsOf(value, path, SHARE_LINK_KEYS));
    if (seen.has(link.id)) throw new ManifestError(`${path}.id`, 'must not be an earlier link’s ID');
    seen.add(link.id);
    return link;
  });
}

/** BU: a version 7 share's fields. */
export const SHARE_KEYS_V7 = ['id', 'secret', 'createdAt', 'made', 'invites', 'pushed', 'ended'];

/** BV1: a share's fields: version 7's, its role keys before its end. */
export const SHARE_KEYS = [...SHARE_KEYS_V7.slice(0, -1), 'roleKeys', 'ended'];

/** BU, BV1: a stored share's fields as the manifest lists them, in its order, each invite's, pushed item's and role key's too. */
export function shareFields(share: ShareRecord): ManifestShare {
  const { id, secret, createdAt, made, invites, pushed, roleKeys, ended } = share;
  return {
    id,
    secret,
    createdAt,
    made,
    invites: invites.map(({ id: invite, email, role, notes, export: exported, endsAt, chapters, claim, key, createdAt: invitedAt, changedAt, sent, removedAt }) => ({
      id: invite,
      email,
      role,
      notes,
      export: exported,
      endsAt,
      chapters: chapters === null ? null : { from: chapters.from, to: chapters.to },
      claim,
      key,
      createdAt: invitedAt,
      changedAt,
      sent,
      removedAt,
    })),
    pushed: pushed.map(({ item, version, hash, holders }) => ({ item, version, hash, holders: [...holders] })),
    roleKeys: roleKeys.map(({ group, epoch, key, holders, withheld, pending }) => ({ group, epoch, key, holders: [...holders], withheld: [...withheld], pending: [...pending] })),
    ended: ended === null ? null : { at: ended.at, why: ended.why },
  };
}

/** BU, BV2: a share of a manifest as book `bookId` holds it, its role keys with their holders. */
export function shareRecordOf(share: ManifestShare, bookId: string): ShareRecord {
  const { id, secret, createdAt, made, invites, pushed, roleKeys, ended } = share;
  return { id, bookId, secret, createdAt, made, invites, pushed, roleKeys, ended };
}

/**
 * BU, BV1: a share's fields, by the store's reader's rules (table SH, `readShare`; part 24b reads a piece's with it). A
 * version 7 share has no role keys, which the reader reads as none (BV5).
 */
export function shareRecordFields(fields: Fields): ManifestShare {
  const record: Record<string, unknown> = { bookId: '0'.repeat(32) };
  for (const key of SHARE_KEYS_V7) record[key] = fields.get(key);
  const roleKeys = fields.get('roleKeys');
  if (roleKeys !== undefined) record['roleKeys'] = roleKeys;
  try {
    return shareFields(readShare(record));
  } catch (error) {
    throw manifestErrorOf(error, fields);
  }
}

/** BV5: a share of a version 7 manifest as it reads: every field but the role keys, which came with version 8. */
function shareV7(share: ManifestShare): ManifestShareV7 {
  const { id, secret, createdAt, made, invites, pushed, ended } = share;
  return { id, secret, createdAt, made, invites, pushed, ended };
}

/** BU, BV1: the shares of a version 8 manifest (`keys` {@link SHARE_KEYS}) or 7, no ID twice (each is the key of the record it came from). */
function readShares(fields: Fields, keys: readonly string[]): ManifestShare[] {
  const seen = new Set<string>();
  return readList(fields, 'shares', (value, path) => {
    const share = shareRecordFields(fieldsOf(value, path, keys));
    if (seen.has(share.id)) throw new ManifestError(`${path}.id`, 'must not be an earlier share’s ID');
    seen.add(share.id);
    return share;
  });
}

/** M11: an image record's fields, by the store's reader's rules (part 16a, V1; part 24b reads a piece's with it). */
export function readImageRecord(value: unknown, path: string): ManifestImageRecord {
  const fields = fieldsOf(value, path, ['sha256', 'format', 'width', 'height', 'size']);
  const sha256 = readHash(fields, 'sha256');
  const format = IMAGE_FORMATS.find((known) => known === fields.get('format'));
  if (format === undefined) throw new ManifestError(fields.name('format'), 'must be "jpeg" or "png"');
  return {
    sha256,
    format,
    width: fields.within('width', 1, KEPT_SIDE.cover),
    height: fields.within('height', 1, KEPT_SIDE.cover),
    size: fields.within('size', 1, Number.MAX_SAFE_INTEGER),
  };
}

/** M11: an image, its file named by its own ID. */
function readImage(value: unknown, path: string): ManifestImage {
  const fields = fieldsOf(value, path, ['id', 'record', 'file']);
  const id = fields.id('id');
  const record = fields.get('record');
  const file = fields.get('file') === null ? null : fields.string('file');
  if (file !== null && IMAGE_PATH.exec(file)?.[1] !== id) throw new ManifestError(fields.name('file'), 'must be the path of the image’s own ID');
  return { id, record: record === null ? null : readImageRecord(record, fields.name('record')), file };
}

/** Each element of the list at `key`, read by `read` with its path. */
function readList<T>(fields: Fields, key: string, read: (value: unknown, path: string) => T): T[] {
  return fields.list(key).map((value, index) => read(value, `${fields.name(key)}[${String(index)}]`));
}

/** M10: version 1's fields, in its order. */
const V1_KEYS = ['format', 'version', 'device', 'book', 'chapters', 'trash', 'conflictCopies', 'snapshots', 'unreadable'];

/** M10′: version 2's fields, in its order. */
const V2_KEYS = ['format', 'version', 'device', 'book', 'chapters', 'trash', 'conflictCopies', 'snapshots', 'images', 'unreadable'];

/** M10″: version 3's fields, in its order. */
const V3_KEYS = ['format', 'version', 'device', 'book', 'chapters', 'trash', 'conflictCopies', 'snapshots', 'images', 'bookNotes', 'unreadable'];

/** B‴6: version 4's fields, in its order. */
const V4_KEYS = ['format', 'version', 'device', 'book', 'chapters', 'trash', 'conflictCopies', 'snapshots', 'images', 'bookNotes', 'comments', 'unreadable'];

/** V5: version 5's fields, in its order. */
const V5_KEYS = ['format', 'version', 'device', 'book', 'chapters', 'trash', 'conflictCopies', 'snapshots', 'images', 'notes', 'unreadable'];

/** V6: version 6's fields, in its order. */
const V6_KEYS = ['format', 'version', 'device', 'book', 'chapters', 'trash', 'conflictCopies', 'snapshots', 'images', 'notes', 'shareLinks', 'unreadable'];

/** BU: version 7's fields, in its order; BV1, TL: version 8's and 9's too (their new fields are inside the records). */
const V7_KEYS = ['format', 'version', 'device', 'book', 'chapters', 'trash', 'conflictCopies', 'snapshots', 'images', 'notes', 'shareLinks', 'shares', 'unreadable'];

/** M10″, B‴6, V5-7, BU, BV, TL: each version's fields. */
const VERSION_KEYS: ReadonlyMap<unknown, readonly string[]> = new Map([
  [BUNDLE_VERSION_1, V1_KEYS],
  [BUNDLE_VERSION_2, V2_KEYS],
  [BUNDLE_VERSION_3, V3_KEYS],
  [BUNDLE_VERSION_4, V4_KEYS],
  [BUNDLE_VERSION_5, V5_KEYS],
  [BUNDLE_VERSION_6, V6_KEYS],
  [BUNDLE_VERSION_7, V7_KEYS],
  [BUNDLE_VERSION_8, V7_KEYS],
  [BUNDLE_VERSION, V7_KEYS],
]);

/** M8′: version 2's refused records. */
const readUnreadableV2 = readUnreadableOf(V2_STORES);

/** M8″: version 3's refused records. */
const readUnreadableV3 = readUnreadableOf(V3_STORES);

/** B‴2: version 4's refused records. */
const readUnreadableV4 = readUnreadableOf(V4_STORES);

/** V5-2, V5-3: version 5's refused records. */
const readUnreadableV5 = readUnreadableOf(V5_STORES);

/** V6-2: version 6's refused records. */
const readUnreadableV6 = readUnreadableOf(V6_STORES);

/** BU, BV, TL: version 7's to 9's refused records. */
const readUnreadable = readUnreadableOf(V7_STORES);

/**
 * M1–M13, M8′, M8″, M10′, M10″, B‴6, V5, V6, BU, BV, TL: reads a manifest file's text; throws {@link ManifestError} for text
 * that isn't JSON or exactly a version 1 to 9 manifest. Field by field, a note's, a comment's, a link's or a share's ID
 * once (M13, M15, V5-6, V6-1, BU) and a reply's thread (V5-6): whether the files it names are in the bundle, and whether its other IDs are unique, are the
 * import's to check (part 24).
 */
export function readManifest(text: string): AnyManifest {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw new ManifestError('', 'must be JSON');
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new ManifestError('', 'must be an object');
  // M1, M10′: the format, then the version, which says which fields there are.
  if (Reflect.get(value, 'format') !== BUNDLE_FORMAT) throw new ManifestError('format', `must be "${BUNDLE_FORMAT}"`);
  const version: unknown = Reflect.get(value, 'version');
  const keys = VERSION_KEYS.get(version);
  if (keys === undefined) throw new ManifestError('version', `must be from ${String(BUNDLE_VERSION_1)} to ${String(BUNDLE_VERSION)}`);
  const fields = fieldsOf(value, '', keys);
  const device = fields.id('device');
  if (version === BUNDLE_VERSION) {
    const book = readBook(fields.get('book'));
    const chapters = readList(fields, 'chapters', readChapter);
    const trash = readList(fields, 'trash', readTrashed);
    const conflictCopies = readList(fields, 'conflictCopies', readCopy);
    const snapshots = readList(fields, 'snapshots', readSnapshot);
    const images = readList(fields, 'images', readImage);
    const notes = readNoteRecords(fields, NOTE_RECORD_KEYS);
    const shareLinks = readShareLinks(fields);
    const shares = readShares(fields, SHARE_KEYS);
    return { format: BUNDLE_FORMAT, version, device, book, chapters, trash, conflictCopies, snapshots, images, notes, shareLinks, shares, unreadable: readList(fields, 'unreadable', readUnreadable) };
  }
  const book = readBookV1(fields.get('book'));
  if (version === BUNDLE_VERSION_8 || version === BUNDLE_VERSION_7 || version === BUNDLE_VERSION_6 || version === BUNDLE_VERSION_5) {
    const chapters = readList(fields, 'chapters', readChapterV5);
    const trash = readList(fields, 'trash', readTrashedV5);
    const conflictCopies = readList(fields, 'conflictCopies', readCopy);
    const snapshots = readList(fields, 'snapshots', readSnapshot);
    const images = readList(fields, 'images', readImage);
    if (version === BUNDLE_VERSION_5) {
      const notes = readNoteRecords(fields, NOTE_RECORD_KEYS_V5).map(noteRecordV5);
      return { format: BUNDLE_FORMAT, version, device, book, chapters, trash, conflictCopies, snapshots, images, notes, unreadable: readList(fields, 'unreadable', readUnreadableV5) };
    }
    if (version === BUNDLE_VERSION_8) {
      const notes = readNoteRecords(fields, NOTE_RECORD_KEYS);
      const shareLinks = readShareLinks(fields);
      const shares = readShares(fields, SHARE_KEYS);
      return { format: BUNDLE_FORMAT, version, device, book, chapters, trash, conflictCopies, snapshots, images, notes, shareLinks, shares, unreadable: readList(fields, 'unreadable', readUnreadable) };
    }
    const notes = readNoteRecords(fields, NOTE_RECORD_KEYS_V6).map(noteRecordV6);
    const shareLinks = readShareLinks(fields);
    if (version === BUNDLE_VERSION_6) {
      return { format: BUNDLE_FORMAT, version, device, book, chapters, trash, conflictCopies, snapshots, images, notes, shareLinks, unreadable: readList(fields, 'unreadable', readUnreadableV6) };
    }
    const shares = readShares(fields, SHARE_KEYS_V7).map(shareV7);
    return { format: BUNDLE_FORMAT, version, device, book, chapters, trash, conflictCopies, snapshots, images, notes, shareLinks, shares, unreadable: readList(fields, 'unreadable', readUnreadable) };
  }
  const chapters = readList(fields, 'chapters', readChapterV1);
  const trash = readList(fields, 'trash', readTrashedV1);
  const conflictCopies = readList(fields, 'conflictCopies', readCopy);
  const snapshots = readList(fields, 'snapshots', readSnapshot);
  if (version === BUNDLE_VERSION_1) {
    return { format: BUNDLE_FORMAT, version, device, book, chapters, trash, conflictCopies, snapshots, unreadable: readList(fields, 'unreadable', readUnreadableV1) };
  }
  const images = readList(fields, 'images', readImage);
  if (version === BUNDLE_VERSION_2) {
    return { format: BUNDLE_FORMAT, version, device, book, chapters, trash, conflictCopies, snapshots, images, unreadable: readList(fields, 'unreadable', readUnreadableV2) };
  }
  const bookNotes = readNotes(fields);
  if (version === BUNDLE_VERSION_3) {
    return { format: BUNDLE_FORMAT, version, device, book, chapters, trash, conflictCopies, snapshots, images, bookNotes, unreadable: readList(fields, 'unreadable', readUnreadableV3) };
  }
  const comments = readComments(fields);
  return { format: BUNDLE_FORMAT, version: BUNDLE_VERSION_4, device, book, chapters, trash, conflictCopies, snapshots, images, bookNotes, comments, unreadable: readList(fields, 'unreadable', readUnreadableV4) };
}
